<template>
  <div :class="classModifiers" class="sp-date-picker">
    <input ref="datepicker" type="hidden" />
  </div>
</template>

<script>
const Mode = {
  Single: "single",
  Range: "range",
  Multiple: "multiple",
};
</script>

<script setup>
import { useEventListener, whenever } from "@vueuse/core";
import flatpickr from "flatpickr";
import { computed, nextTick, onMounted, onUnmounted, ref, toRef, useAttrs, useTemplateRef, watch } from "vue";
import { useAriaLabel } from "#ce/composables/useAriaLabel";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useFormControl } from "#ce/composables/useFormControl";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { t } from "#ce/shared/forms-i18n";
import { isPresent, toBoolean } from "#ce/utils/props";
import { props as datePickerProps } from "./props.js";
import { formAssociatedHost } from "#ce/framework/utils";

// sp-date-picker is a genuine form control: match :disabled and respond to <fieldset disabled>.
defineOptions(formAssociatedHost({ inheritAttrs: false }));

const emit = defineEmits(["input", "change", "update-model-value", "visible-range", "pick"]);
const { emitBubbling } = useHostEmit();

const props = defineProps(datePickerProps);

// Disabled is governed by the form-control composable: presence-based own
// attribute plus ancestor <fieldset disabled>, applied to the flatpickr input.
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

const datepicker = useTemplateRef("datepicker");

const { breakpointModifiers, isSmScreen } = useBreakpoints();
const navText = computed(() => toBoolean(props.navText));
const shortWeekdays = computed(() => toBoolean(props.shortWeekdays));

const classModifiers = computed(() => {
  const modifiers = [...breakpointModifiers.value];

  if (navText.value) {
    modifiers.push("--nav-text");
  }

  if (toBoolean(props.noTodayMark)) {
    modifiers.push("--no-today-mark");
  }

  return modifiers;
});

const { exposeProperties, exposeMethods } = useExpose();

const calendar = ref(null);
const altInput = computed(() => calendar.value?.altInput);

// The `value` attribute seeds the calendar and names what a form reset restores; the `value`
// property is the live selection. A declared prop would install a non-configurable host accessor
// that returns the seed forever.
const attrs = useAttrs();
const defaultValue = ref(attrs.value);
const model = ref(defaultValue.value);

// The grid is in shadow DOM, so a host `aria-label` names nothing a reader reaches. The calendar
// element takes the name, and `role="group"` is what makes a name on it carry.
const calendarContainer = computed(() => calendar.value?.calendarContainer ?? null);

const { ariaLabel } = useAriaLabel(calendarContainer, {
  label: () => attrs.ariaLabel,
  labelledby: toRef(props, "ariaLabelledby"),
});

watch(
  [calendarContainer, ariaLabel],
  ([container, label]) => {
    if (!container) {
      return;
    }

    container.setAttribute("role", "group");

    if (label) {
      container.setAttribute("aria-label", label);
    } else {
      container.removeAttribute("aria-label");
    }
  },
  { immediate: true },
);

function focusSelectedDay() {
  const container = calendar.value?.calendarContainer;
  // Prefers the selected day, then the first day of the grid.
  const day = container?.querySelector(".flatpickr-day.selected") ?? container?.querySelector(".flatpickr-day");

  day?.focus();
}

// The day cells are in shadow DOM, so `focus` is a caller's only way to reach one.
exposeMethods({ focus: focusSelectedDay });

exposeProperties({
  displayText: {
    configurable: true,
    get: () => altInput.value?.value,
  },
  value: {
    configurable: true,
    get: () => model.value,
    set: setSelection,
  },
  defaultValue: {
    configurable: true,
    get: () => defaultValue.value,
  },
});

watch(
  () => attrs.value,
  (value) => {
    defaultValue.value = value;
    setSelection(value);
  },
);
watch(model, (value) => {
  if (!value) {
    clearCalendar();
  }
});

// Drives the calendar as well as the model, so a value written from outside is reflected in the
// rendered dates. `false` suppresses flatpickr's own change hook, which would re-enter here.
function setSelection(value) {
  model.value = value ?? null;
  calendar.value?.setDate(model.value, false);
  adoptRenderedSelection();
  keepLastMonthWithinMax();
}

// Months side by side end on the month of `maxDate` at the latest, so none of them is wholly out of
// reach.
function keepLastMonthWithinMax() {
  const instance = calendar.value;
  const max = instance?.config.maxDate;
  const months = instance?.config.showMonths ?? 1;

  if (!max || months < 2) {
    return;
  }

  const shownMonth = instance.currentYear * 12 + instance.currentMonth;
  const lastAllowedFirstMonth = max.getFullYear() * 12 + max.getMonth() - (months - 1);

  if (shownMonth > lastAllowedFirstMonth) {
    instance.changeMonth(lastAllowedFirstMonth - shownMonth);
  }
}

// flatpickr writes the selection into its own input in `dateFormat`, joined the way each mode
// expects. Reading that back keeps the model a string in the configured format whatever was
// written — a `Date`, a number — and keeps a local time the clock skips equal to the one shown.
function adoptRenderedSelection() {
  if (calendar.value?.selectedDates?.length) {
    model.value = calendar.value.input.value;
  }
}

// The calendar sits in the shadow root, where the outer form cannot see it, so the element submits
// through ElementInternals under its own name.
const { mirrorFormValue, mirrorValueMissing, onFormReset } = useForm();
const formValue = computed(() => (model.value === undefined || model.value === null ? "" : String(model.value)));

mirrorFormValue(formValue);

onFormReset(() => setSelection(defaultValue.value));

// A required picker is satisfied only once a date is chosen.
const isRequired = computed(() => isPresent(props.required));
const validationMessage = computed(() => props.requiredMessage || t("date_value_missing"));

mirrorValueMissing(() => isRequired.value && formValue.value === "", validationMessage, datepicker);

// clear(false): flatpickr's default fires its own change, which re-enters handleChange and
// reports a scripted clear as user input, twice.
function clearCalendar() {
  calendar.value?.clear(false);
}

// A picker that can carry a time has to show it, so the default display format grows one when the
// caller did not name a format themselves.
const displayFormat = computed(() => {
  if (props.displayFormat) {
    return props.displayFormat;
  }

  return toBoolean(props.enableTime) ? "F j, Y h:i K" : "F j, Y";
});

const isRangeMode = computed(() => toBoolean(props.range));
const isMultipleMode = computed(() => toBoolean(props.multiple));

const mode = computed(() => {
  if (isMultipleMode.value) {
    return Mode.Multiple;
  }
  if (isRangeMode.value) {
    return Mode.Range;
  }
  return props.mode;
});

const showMonths = computed(() => (isSmScreen.value ? 1 : Math.max(1, Number(props.showMonths) || 1)));

function initializeFlatpickr() {
  const config = {
    mode: mode.value,
    showMonths: showMonths.value,
    inline: toBoolean(props.inline),
    enableTime: toBoolean(props.enableTime),
    altInput: true,
    altFormat: displayFormat.value,
    altInputClass: "flatpickr-alt-input",
    onChange: handleChange,
    prevArrow: navText.value ? "" : buildArrowIcon("arrow-left-filled"),
    nextArrow: navText.value ? "" : buildArrowIcon("arrow-right-filled"),
  };

  if (shortWeekdays.value) {
    config.locale = { weekdays: twoLetterWeekdays() };
  }

  if (props.valueFormat) {
    config.dateFormat = props.valueFormat;
  }

  if (props.minDate !== undefined) {
    config.minDate = props.minDate;
  }

  if (props.maxDate !== undefined) {
    config.maxDate = props.maxDate;
  }

  config.onDayCreate = (_dates, _dateString, instance, dayElement) => {
    if (props.dayTestId) {
      dayElement.setAttribute("data-testid", props.dayTestId);
    }

    const state = dayStates.value[instance.formatDate(dayElement.dateObj, "Y-m-d")];

    if (state) {
      dayElement.setAttribute("data-day-state", state);
    } else {
      dayElement.removeAttribute("data-day-state");
    }
  };

  config.onMonthChange = scheduleVisibleRange;
  config.onYearChange = scheduleVisibleRange;

  calendar.value = flatpickr(datepicker.value, config);
  calendar.value.setDate(model.value, false);
  adoptRenderedSelection();
  jumpToOpenDate();
  keepLastMonthWithinMax();
  scheduleVisibleRange();

  if (toBoolean(props.focusOnReady)) {
    // flatpickr rebuilds the day cells as it finishes initialising, so a cell focused during init is
    // replaced; the focus lands once the grid it belongs to is the rendered one.
    nextTick(focusSelectedDay);
  }

  if (props.inputTestId && calendar.value.altInput) {
    calendar.value.altInput.setAttribute("data-testid", props.inputTestId);
  }

  annotateMonthControls();

  applyDisabledState(disabled.value);
}

/**
 * Reflects the disabled state onto flatpickr's generated input, prevents the
 * calendar from opening, and closes it if it is already open (inline calendars
 * stay rendered, but `handleChange` is guarded below). CSS dimming is handled
 * separately via :host(:disabled).
 *
 * @param {boolean} isDisabled
 */
function applyDisabledState(isDisabled) {
  if (!calendar.value) {
    return;
  }

  if (calendar.value.altInput) {
    calendar.value.altInput.disabled = isDisabled;
  }
  calendar.value.input.disabled = isDisabled;
  calendar.value.set("clickOpens", !isDisabled);

  if (isDisabled) {
    calendar.value.close();
  }
}

watch(disabled, applyDisabledState);

watch(
  () => [props.minDate, props.maxDate],
  ([min, max]) => {
    calendar.value?.set("minDate", min);
    calendar.value?.set("maxDate", max);
    keepLastMonthWithinMax();
  },
);

// A format is a setting flatpickr re-reads. The selection is carried across as Date objects,
// because the model still reads in the format being replaced.
watch([displayFormat, () => props.valueFormat], () => {
  if (!calendar.value) {
    return;
  }

  const selected = [...(calendar.value.selectedDates ?? [])];

  calendar.value.set("altFormat", displayFormat.value);
  calendar.value.set("dateFormat", props.valueFormat || defaultValueFormat());
  calendar.value.setDate(selected, false);
  adoptRenderedSelection();
});

// What flatpickr itself would use for `dateFormat`, so dropping `value-format` returns to it
// rather than leaving the format it was last given.
function defaultValueFormat() {
  return toBoolean(props.enableTime) ? "Y-m-d H:i" : "Y-m-d";
}

// The time footer, the selection mode and the month count are part of what flatpickr builds, not
// settings it reads, so changing any of them needs the calendar built again. The model carries the
// selection across.
watch([() => props.enableTime, mode, showMonths, navText, shortWeekdays], () => {
  if (!calendar.value) {
    return;
  }

  calendar.value.destroy();
  calendar.value = null;
  initializeFlatpickr();
});

// `openToDate` is applied as a jump, which flatpickr treats as navigation: `false` leaves the
// selection alone.
function jumpToOpenDate() {
  if (props.openToDate === undefined) {
    return;
  }

  calendar.value?.jumpToDate(props.openToDate, false);
  // `jumpToDate` suppresses flatpickr's own month hooks, and a consumer fetching for the visible
  // days needs to hear about a grid this moved just as much as one the user moved.
  scheduleVisibleRange();
}

watch(() => props.openToDate, jumpToOpenDate);

// A JSON attribute carries the same map for a caller that has only attributes to work with.
const dayStates = computed(() => {
  if (typeof props.dayStates !== "string") {
    return props.dayStates ?? {};
  }

  try {
    return JSON.parse(props.dayStates) ?? {};
  } catch {
    return {};
  }
});

watch(dayStates, () => calendar.value?.redraw());

// The rendered grid runs past the month on both ends, and a consumer fetching for what is on screen
// needs those days too.
// Crossing a year boundary fires onYearChange before the grid is rebuilt and onMonthChange after,
// so reading the cells on each would report the old grid and then the new one. Reading once the
// current task has finished leaves one emission describing what is rendered.
let visibleRangePending = false;

function scheduleVisibleRange() {
  if (visibleRangePending) {
    return;
  }

  visibleRangePending = true;
  nextTick(() => {
    visibleRangePending = false;
    emitVisibleRange();
  });
}

function emitVisibleRange() {
  const days = calendar.value?.days?.querySelectorAll(".flatpickr-day");

  if (!days?.length) {
    return;
  }

  const asDate = (cell) => calendar.value.formatDate(cell.dateObj, "Y-m-d");

  emit("visible-range", { start: asDate(days[0]), end: asDate(days[days.length - 1]) });
}

const monthLabels = computed(() => ({ previous: t("date_previous_month"), next: t("date_next_month") }));
const monthTexts = computed(() => ({ previous: t("date_prev"), next: t("date_next") }));

function twoLetterWeekdays() {
  const { weekdays } = flatpickr.l10ns.default;

  return { longhand: weekdays.longhand, shorthand: weekdays.shorthand.map(twoLetters) };
}

function twoLetters(name) {
  return name.slice(0, 2);
}

// The shared forms bundle is fetched, so a client's own names arrive after the calendar is built.
watch([monthLabels, monthTexts], annotateMonthControls);

/**
 * flatpickr renders each month-paging control as a `<span>` with no role, no
 * accessible name and `tabindex="-1"`. Each one takes a button role, a tab
 * stop, a translated name, the caller's test id, and the Enter and Space keys
 * a button answers.
 */
function annotateMonthControls() {
  const container = calendar.value?.calendarContainer;
  if (!container) {
    return;
  }

  const controls = [
    {
      selector: ".flatpickr-prev-month",
      label: monthLabels.value.previous,
      text: monthTexts.value.previous,
      testId: props.prevMonthTestId,
    },
    {
      selector: ".flatpickr-next-month",
      label: monthLabels.value.next,
      text: monthTexts.value.next,
      testId: props.nextMonthTestId,
    },
  ];

  for (const { selector, label, text, testId } of controls) {
    const control = container.querySelector(selector);
    if (!control) {
      continue;
    }

    if (navText.value) {
      control.textContent = text;
    }

    control.setAttribute("role", "button");
    control.setAttribute("tabindex", "0");
    control.setAttribute("aria-label", label);
    if (testId) {
      control.setAttribute("data-testid", testId);
    }
    control.addEventListener("keydown", activateOnEnterOrSpace);
  }
}

function activateOnEnterOrSpace(event) {
  if (event.key !== "Enter" && event.key !== " ") {
    return;
  }

  event.preventDefault();
  event.currentTarget.click();
}

function buildArrowIcon(name) {
  return `<sp-icon name='${name}' fill-color='var(--date-picker-icon-arrow-color)'></sp-icon>`;
}

/**
 * We wait for the calendar to become available and then delegate the click event from the navigation
 * icons to the parent element.
 *
 * This is necessary because flatpickr does not detect the click event on the icon.
 */
whenever(calendar, (instance) => {
  if (navText.value) {
    return;
  }

  delegateIconEvent(instance.prevMonthNav);
  delegateIconEvent(instance.nextMonthNav);
});

/**
 * Delegates the click event from the icon to the parent element.
 *
 * @param {HTMLElement} parent - The parent element to which the event should be delegated.
 */
function delegateIconEvent(parent) {
  const target = parent.querySelector("sp-icon");
  useEventListener(target, "click", () => parent.dispatchEvent(new PointerEvent("click", { bubbles: true })));
}

function handleChange(selectedDates, value, instance) {
  // A disabled date picker must not mutate form state, even via an inline or
  // already-rendered calendar whose day cells stay clickable.
  if (disabled.value) {
    return;
  }

  // Every click reports the days picked so far, the first end of an unfinished range included.
  emit(
    "pick",
    selectedDates.map((date) => instance.formatDate(date, instance.config.dateFormat)),
  );

  // If the range mode is enabled, but the user has not selected two dates, do not update the model value.
  if (isRangeMode.value && selectedDates.length !== 2) {
    return;
  }

  model.value = value;
  emitBubbling("input", value);
  emit("update-model-value", value);
  emitBubbling("change", value);
}

onMounted(() => {
  nextTick(initializeFlatpickr);
});

onUnmounted(() => {
  calendar.value?.destroy();
});
</script>

<style lang="scss">
@use "flatpickr/dist/flatpickr.min.css";

// Disabled styling driven by the native :disabled state (own attribute or an
// ancestor <fieldset disabled>), no JS required.
:host(:disabled) {
  cursor: not-allowed;
  opacity: 0.6;
}

.sp-date-picker {
  --date-picker-icon-arrow-color: var(--sp-comp-date-picker-arrow-icon-color, var(--sp-sys-color-primary));

  &.--is-sm-screen {
    .flatpickr-calendar,
    .flatpickr-rContainer {
      display: block !important;
      width: 100% !important;
    }
    .dayContainer,
    .flatpickr-days {
      width: 100% !important;
      min-width: 100% !important;
      max-width: 100% !important;
    }
    .flatpickr-day {
      max-width: 14.2857143% !important; // = 100% / 7 days
    }
  }
}

// flatpickr overrides

.flatpickr-alt-input {
  display: none;
}

// flatpickr sizes the calendar and its inner containers with inline widths, which overflow any
// container narrower than they are — a menu sized to its activator, say. The max-widths here and on
// `.flatpickr-calendar` below let them shrink to what they are given.
.flatpickr-rContainer,
.flatpickr-days,
.dayContainer {
  min-width: 0;
  max-width: 100%;
}

// flatpickr writes a multi-month calendar's width inline once, when it is built, and a calendar built
// hidden measures zero. Each month keeps flatpickr's own day-grid width instead.
.flatpickr-calendar.multiMonth {
  width: max-content !important;

  .dayContainer,
  .flatpickr-weekdaycontainer {
    margin-inline-end: var(--sp-comp-date-picker-month-margin-inline-end, 0);
  }

  .flatpickr-weekdaycontainer {
    flex: none;
    width: var(--sp-comp-date-picker-month-width, 307.875px);
  }

  .dayContainer + .dayContainer {
    box-shadow: -1px 0 0 var(--sp-comp-date-picker-month-divider-color, #e6e6e6);
  }

  .flatpickr-rContainer,
  .flatpickr-days,
  .dayContainer {
    max-width: none;
  }

  .flatpickr-days {
    width: auto !important;
  }
}

.flatpickr-calendar {
  max-width: 100%;

  --selected-bg: var(--sp-comp-date-picker-selected-state-bg, var(--sp-sys-color-primary)) !important;
  --selected-text-color: var(
    --sp-comp-date-picker-selected-state-text-color,
    var(--sp-sys-color-on-primary)
  ) !important;
  --selected-border-color: var(--sp-comp-date-picker-selected-state-border-color, var(--selected-bg));

  box-shadow: none !important;
  padding: var(--sp-comp-date-picker-padding, 0) !important;
  margin: 0 !important;
  font-family: var(--sp-sys-font-family-normal);
  font-weight: var(--sp-sys-font-weight-normal);

  .dayContainer {
    width: var(--sp-comp-date-picker-month-width, 307.875px);
  }

  .flatpickr-day {
    display: flex;
    align-items: center;
    max-width: var(--sp-comp-date-picker-day-size, 39px);
    height: var(--sp-comp-date-picker-day-size, 39px);
    line-height: var(--sp-comp-date-picker-day-size, 39px);
    color: var(--sp-comp-date-picker-day-color, var(--sp-sys-color-on-surface));
    font-size: var(--sp-comp-date-picker-day-font-size, inherit);
  }

  // Availability states, set from `dayStates`. Only `high` and `low` are painted: a caller reports
  // `none` for a day it has looked at and found empty, which is every in-range day on a provider
  // with no slots, and colouring those says the opposite of what the state means. A selected or
  // disabled day keeps its own treatment, so the fill applies to the days still offering a choice.
  .flatpickr-day[data-day-state="high"]:not(.selected, .flatpickr-disabled),
  .flatpickr-day[data-day-state="low"]:not(.selected, .flatpickr-disabled) {
    --day-state-color: var(
      --sp-comp-date-picker-day-state-color,
      var(--sp-comp-date-picker-day-color, var(--sp-sys-color-on-surface))
    );

    color: var(--day-state-color);
    font-weight: var(--sp-comp-date-picker-available-day-font-weight, inherit);
    transition: all 0.3s ease-out;
  }

  // The two fills fall back to literals: `primary-container` and `surface-variant` resolve to the
  // same shade on some domains, which leaves the two states indistinguishable.
  .flatpickr-day[data-day-state="high"]:not(.selected, .flatpickr-disabled) {
    --day-state-bg: var(--sp-comp-date-picker-day-state-high-bg, #b2d9dc);

    background-color: var(--day-state-bg);
  }

  .flatpickr-day[data-day-state="low"]:not(.selected, .flatpickr-disabled) {
    --day-state-bg: var(--sp-comp-date-picker-day-state-low-bg, #e4f0f2);

    background-color: var(--day-state-bg);
  }

  .flatpickr-day.prevMonthDay,
  .flatpickr-day.nextMonthDay {
    color: var(--sp-comp-date-picker-adjacent-month-color, var(--sp-sys-color-outline-variant));
  }

  .flatpickr-day.selected,
  .flatpickr-day.startRange,
  .flatpickr-day.endRange,
  .flatpickr-day.selected.inRange,
  .flatpickr-day.startRange.inRange,
  .flatpickr-day.endRange.inRange,
  .flatpickr-day.selected:focus,
  .flatpickr-day.startRange:focus,
  .flatpickr-day.endRange:focus,
  .flatpickr-day.selected:hover,
  .flatpickr-day.startRange:hover,
  .flatpickr-day.endRange:hover,
  .flatpickr-day.selected.prevMonthDay,
  .flatpickr-day.startRange.prevMonthDay,
  .flatpickr-day.endRange.prevMonthDay,
  .flatpickr-day.selected.nextMonthDay,
  .flatpickr-day.startRange.nextMonthDay,
  .flatpickr-day.endRange.nextMonthDay {
    background: var(--selected-bg) !important;
    border-color: var(--selected-border-color) !important;
    color: var(--selected-text-color) !important;
    font-weight: var(--sp-comp-date-picker-selected-day-font-weight, inherit);
  }

  .flatpickr-day.inRange,
  .flatpickr-day.prevMonthDay.inRange,
  .flatpickr-day.nextMonthDay.inRange,
  .flatpickr-day.today.inRange,
  .flatpickr-day.prevMonthDay.today.inRange,
  .flatpickr-day.nextMonthDay.today.inRange,
  .flatpickr-day:hover,
  .flatpickr-day.prevMonthDay:hover,
  .flatpickr-day.nextMonthDay:hover,
  .flatpickr-day:focus,
  .flatpickr-day.prevMonthDay:focus,
  .flatpickr-day.nextMonthDay:focus {
    --background: var(--sp-comp-date-picker-in-range-bg, var(--sp-sys-color-primary-container));
    border-color: var(--background) !important;
    background: var(--background) !important;
    color: var(--sp-comp-date-picker-in-range-text-color, var(--sp-sys-color-on-surface)) !important;
    box-shadow:
      -5px 0 0 var(--background),
      5px 0 0 var(--background);
  }

  .flatpickr-day:hover,
  .flatpickr-day.prevMonthDay:hover,
  .flatpickr-day.nextMonthDay:hover,
  .flatpickr-day:focus,
  .flatpickr-day.prevMonthDay:focus,
  .flatpickr-day.nextMonthDay:focus {
    --background: var(--sp-comp-date-picker-day-hover-bg, var(--sp-sys-color-primary-container));
    box-shadow: none !important;
  }

  // Availability outranks the pointer: the state fill is what the user is scanning the grid for, and
  // the hover fill above would report a busy day as an ordinary one for as long as the pointer sits
  // on it. Both declarations carry `!important`, so this has to as well.
  .flatpickr-day[data-day-state="high"]:hover:not(.selected, .flatpickr-disabled),
  .flatpickr-day[data-day-state="high"]:focus:not(.selected, .flatpickr-disabled),
  .flatpickr-day[data-day-state="low"]:hover:not(.selected, .flatpickr-disabled),
  .flatpickr-day[data-day-state="low"]:focus:not(.selected, .flatpickr-disabled) {
    background: var(--day-state-bg) !important;
    border-color: var(--day-state-bg) !important;
    color: var(--day-state-color) !important;
  }

  .flatpickr-day.prevMonthDay:hover,
  .flatpickr-day.nextMonthDay:hover {
    color: var(--sp-comp-date-picker-adjacent-month-hover-color, var(--sp-sys-color-on-surface)) !important;
  }

  .flatpickr-day.selected.startRange + .endRange:not(:nth-child(7n + 1)),
  .flatpickr-day.startRange.startRange + .endRange:not(:nth-child(7n + 1)),
  .flatpickr-day.endRange.startRange + .endRange:not(:nth-child(7n + 1)) {
    box-shadow: -10px 0 0 var(--selected-bg);
  }

  .flatpickr-day.today {
    border: 2px solid var(--sp-comp-date-picker-today-border-color, var(--sp-sys-color-danger)) !important;
  }

  // A day outside min/max is not selectable, and has to read that way. It sits last of the day rules
  // because a disabled day can also be today, an adjacent-month day, or carry a stale selection, and
  // each of those would otherwise repaint it.
  .flatpickr-day.flatpickr-disabled,
  .flatpickr-day.flatpickr-disabled:hover,
  .flatpickr-day.flatpickr-disabled:focus {
    color: var(--sp-comp-date-picker-disabled-day-color, var(--sp-sys-color-outline-variant)) !important;
    background: transparent !important;
    border-color: transparent !important;
    box-shadow: none !important;
    cursor: default;
  }

  .flatpickr-current-month {
    position: relative;
    left: unset;
    width: 100%;
    display: flex;
    align-items: center;
    padding: 0;
    font: var(--sp-sys-typescale-small-bold-short) !important;
    font-size: var(--sp-comp-date-picker-header-font-size, inherit) !important;
    font-weight: var(--sp-comp-date-picker-header-font-weight, inherit) !important;
    color: var(--sp-comp-date-picker-header-color, inherit);
    text-transform: var(--sp-comp-date-picker-header-text-transform, none);

    .flatpickr-monthDropdown-months {
      padding: 0;
      margin-left: -0.35ch;
    }

    .flatpickr-monthDropdown-months,
    .cur-month,
    .cur-year {
      color: inherit;
      font-weight: var(
        --sp-comp-date-picker-header-font-weight,
        var(--sp-sys-typescale-small-bold-font-weight)
      ) !important;
    }
  }

  .flatpickr-months .flatpickr-prev-month,
  .flatpickr-months .flatpickr-next-month {
    position: relative;
    padding: 0;
    transition: all 0.3s ease-in-out;

    &:hover {
      opacity: 0.7;
    }
  }

  .flatpickr-months {
    margin-bottom: var(--sp-comp-date-picker-header-margin-bottom, 0) !important;

    .flatpickr-month {
      order: 1;
    }

    .flatpickr-prev-month {
      order: 2;
    }

    .flatpickr-next-month {
      order: 3;
    }
  }
  .--nav-text & .flatpickr-months {
    align-items: center;

    .flatpickr-current-month {
      justify-content: center;
    }

    .flatpickr-prev-month {
      order: 0;
    }

    .flatpickr-prev-month.flatpickr-disabled,
    .flatpickr-next-month.flatpickr-disabled {
      display: block;
      pointer-events: none;
    }

    .flatpickr-prev-month,
    .flatpickr-next-month {
      height: auto;
      color: var(--sp-comp-date-picker-nav-text-color, var(--sp-sys-color-link));
      font: inherit;
    }
  }

  .--no-today-mark & .flatpickr-day.today:not(.selected, .startRange, .endRange) {
    border-color: transparent !important;
  }

  .flatpickr-weekdays {
    border-bottom: var(--sp-comp-date-picker-weekdays-border-width, 1px) solid
      var(--sp-comp-date-picker-weekdays-border-color, var(--sp-sys-color-outline-variant));

    .flatpickr-weekday {
      color: var(--sp-comp-date-picker-weekdays-color, var(--sp-sys-color-on-surface));
      font-size: var(--sp-comp-date-picker-weekdays-font-size, inherit);
      font-weight: var(--sp-comp-date-picker-weekdays-font-weight, inherit);
    }
  }
}
</style>
